import confetti from "canvas-confetti";
import { animate, stagger } from "motion";
import "./styles.css";
import { AnalyticsAdapter } from "./analytics";
import {
  amountBand,
  ARMS,
  canonicalizeMarketTestSearch,
  chooseWish,
  createSplit,
  durationBand,
  isExperimentArm,
  sanitizeSource,
} from "./experiment";
import { createFirebaseBridge } from "./firebase";
import {
  currencyForLocale,
  formatCurrency,
  formatPeopleCount,
  formatRitualCount,
  localeFromPathname,
  normalizeLocalizedDecimal,
  resolveLocale,
  translate,
  translateWish,
} from "./i18n";
import type { CopyKey } from "./i18n";
import {
  clearDemoState,
  createPot,
  openDemoSession,
  setNickname,
  setPotKeepDecision,
  setPotSplit,
} from "./state";
import { SAMPLE_CIRCLES } from "./ritual-circle";
import type { CircleRitual } from "./ritual-circle";
import type { PotRecord, SplitResult, WishMessage } from "./types";

const DEMO_VERSION = "0.7.0-market-test";
const search = new URLSearchParams(window.location.search);
const localeResolution = resolveLocale(search.get("lang"), [
  ...navigator.languages,
  navigator.language,
], localeFromPathname(window.location.pathname));
const locale = localeResolution.locale;
const currency = currencyForLocale(locale);
const t = (key: CopyKey, params?: Readonly<Record<string, string | number>>): string =>
  translate(locale, key, params);
document.documentElement.lang = locale;
document.documentElement.dataset.locale = locale;
document.documentElement.dataset.localeSource = localeResolution.source;
document.title = t("metaTitle");
document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", t("metaDescription"));
const debug = search.get("debug") === "1";
const explicitQa = search.get("qa") === "1";
const stateTestMode = debug || explicitQa || isExperimentArm(search.get("arm")) || search.has("reset");
const loopbackHost = ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);
const analyticsTestMode = stateTestMode || loopbackHost;
const reducedMotion = search.get("motion") === "reduce" || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
document.documentElement.classList.toggle("reduce-motion", reducedMotion);
const session = openDemoSession(search, stateTestMode, currency.code);

const factor = ARMS[session.state.arm];
const source = sanitizeSource(search.get("source"));
const canonicalSearch = canonicalizeMarketTestSearch(search);
if (canonicalSearch !== window.location.search.slice(1)) {
  const canonicalUrl = new URL(window.location.href);
  canonicalUrl.search = canonicalSearch;
  window.history.replaceState(null, "", canonicalUrl);
}
const runtimeConfig = window.MONEY_RITUALS_CONFIG ?? {};
const firebase = createFirebaseBridge(runtimeConfig.firebase, {
  testMode: analyticsTestMode,
  analyticsEnabled: runtimeConfig.firebaseAnalyticsEnabled,
  feedbackEnabled: runtimeConfig.firebaseFeedbackEnabled,
});
const analytics = new AnalyticsAdapter({
  demoVersion: DEMO_VERSION,
  sessionId: createSessionId(),
  participantId: session.state.participantId,
  arm: session.state.arm,
  source,
  isReturnSession: session.isReturnSession,
  debug,
  testMode: analyticsTestMode,
}, firebase);

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("App mount point not found.");

app.innerHTML = [
  "<div class='atmosphere' aria-hidden='true'></div>",
  "<div class='app-shell'>",
  "  <header class='topbar'>",
  `    <button class='icon-button' id='privacy-button' type='button' aria-label='${t("aboutPlayTest")}' aria-expanded='false' aria-controls='privacy-panel'><i class='ph ph-info' aria-hidden='true'></i></button>`,
  `    <button class='wordmark' id='home-link' type='button'>${t("brand")}</button>`,
  "    <div class='profile-slot'><span class='profile-chip' id='profile-chip' role='status' aria-live='polite' hidden></span></div>",
  "  </header>",
  "  <main>",
  "    <section class='screen home-screen is-active' id='home-screen' data-stage='home'>",
  `      <h1 tabindex='-1'>${t("pickRitual")}</h1>`,
  "      <div class='ritual-picker'>",
  "        <button class='ritual-choice frog-choice' id='choose-frog' type='button'>",
  "          <img src='./assets/generated/money-frog-v1.webp' alt='' width='900' height='936' />",
  `          <span>${t("moneyFrog")}</span>`,
  "        </button>",
  "        <button class='ritual-choice tree-choice' id='choose-tree' type='button'>",
  "          <img src='./assets/generated/money-branch-v1.webp' alt='' width='640' height='960' />",
  `          <span>${t("moneyTree")}</span>`,
  "        </button>",
  "      </div>",
  "    </section>",
  "",
  "    <section class='screen amount-screen' id='amount-screen' data-stage='amount' hidden>",
  `      <button class='back-button' type='button' data-back-home><i class='ph ph-arrow-left' aria-hidden='true'></i> ${t("back")}</button>`,
  "      <div class='amount-mascot' aria-hidden='true'><img src='./assets/generated/money-frog-v1.webp' alt='' width='900' height='936' /></div>",
  `      <h1 tabindex='-1'>${t("funMoneyTitle")}</h1>`,
  "      <form class='amount-form' id='pot-form' novalidate>",
  "        <label class='amount-control' for='pot-amount'>",
  `          <span aria-hidden='true'>${currency.symbol}</span>`,
  `          <input id='pot-amount' name='amount' type='text' inputmode='decimal' enterkeyhint='go' autocomplete='off' value='75' aria-label='${t("funMoneyAmountLabel")}' aria-describedby='amount-error' required />`,
  `          <small>${currency.code}</small>`,
  "        </label>",
  "        <p class='field-error' id='amount-error' role='alert'></p>",
  `        <div class='preset-row' aria-label='${t("quickAmountsLabel")}'>`,
  `          <button type='button' class='preset' data-amount='40'>${formatMoney(4_000)}</button>`,
  `          <button type='button' class='preset is-selected' data-amount='75'>${formatMoney(7_500)}</button>`,
  `          <button type='button' class='preset' data-amount='100'>${formatMoney(10_000)}</button>`,
  `          <button type='button' class='preset' data-amount='150'>${formatMoney(15_000)}</button>`,
  "        </div>",
  `        <button class='primary-button' id='begin-button' type='submit'>${t("letsPlay")}</button>`,
  "      </form>",
  "    </section>",
  "",
  "    <section class='screen frog-screen' id='frog-screen' data-stage='frog' hidden>",
  `      <button class='back-button' type='button' data-back-home><i class='ph ph-arrow-left' aria-hidden='true'></i> ${t("back")}</button>`,
  `      <h1 tabindex='-1' id='frog-title'>${t("tapFive")}</h1>`,
  "      <div class='frog-stage' id='frog-stage'>",
  "        <button class='frog-button' id='frog-button' type='button' aria-describedby='frog-instruction'>",
  `          <img class='frog-frame frog-closed' src='./assets/generated/money-frog-v1.webp' alt='${t("frogAlt")}' width='900' height='936' />`,
  "          <img class='frog-frame frog-open' src='./assets/generated/money-frog-open-v1.webp' alt='' width='900' height='900' />",
  "          <span class='mouth-anchor' id='mouth-anchor' aria-hidden='true'></span>",
  "        </button>",
  "        <div class='pat-effect-layer' id='pat-effect-layer' aria-hidden='true'></div>",
  `        <div class='bubble-field' id='bubble-field' hidden aria-label='${t("bubbleFieldLabel")}'>`,
  `          <button class='ritual-bubble bubble-one' type='button' data-bubble='1' aria-label='${t("popBubble", { number: 1 })}'></button>`,
  `          <button class='ritual-bubble bubble-two' type='button' data-bubble='2' aria-label='${t("popBubble", { number: 2 })}'></button>`,
  `          <button class='ritual-bubble bubble-three' type='button' data-bubble='3' aria-label='${t("popBubble", { number: 3 })}'></button>`,
  "        </div>",
  "      </div>",
  "      <div class='pat-panel'>",
  `        <p id='frog-instruction'>${t("patFrog")}</p>`,
  "        <div class='pat-progress' id='pat-progress' aria-hidden='true'><span></span><span></span><span></span><span></span><span></span></div>",
  "        <strong id='pat-copy'>0 / 5</strong>",
  "      </div>",
  "      <p class='sr-only' id='frog-live' aria-live='polite'></p>",
  "    </section>",
  "",
  "    <section class='screen result-screen' id='result-screen' data-stage='result' hidden>",
  `      <button class='back-button' type='button' data-back-home><i class='ph ph-house' aria-hidden='true'></i> ${t("rituals")}</button>`,
  `      <h1 tabindex='-1'>${t("frogApproved")}</h1>`,
  `      <div class='split-board' aria-label='${t("splitLabel")}'>`,
  `        <div class='split-value enjoy-value'><span>${t("enjoy")}</span><strong id='enjoy-amount'>${formatMoney(0)}</strong><small id='enjoy-percent'>0%</small></div>`,
  `        <div class='split-value keep-value'><span>${t("keep")}</span><strong id='keep-amount'>${formatMoney(0)}</strong><small id='keep-percent'>0%</small></div>`,
  "      </div>",
  "      <section class='ritual-circle' id='frog-circle' aria-labelledby='frog-circle-label'>",
  `        <div class='circle-heading'><div><p id='frog-circle-label'></p><strong id='frog-circle-count'>0</strong></div><small>${t("topFive")}</small></div>`,
  "        <ol class='circle-list' id='frog-circle-list' role='list'></ol>",
  "      </section>",
  "      <div class='keep-actions'>",
  `        <p>${t("keepSetAside")}</p>`,
  `        <button class='primary-button' id='confirm-keep' type='button'>${t("yesKept")}</button>`,
  `        <button class='secondary-button' id='decline-keep' type='button'>${t("maybeLater")}</button>`,
  "      </div>",
  "      <p class='sr-only' id='split-live' aria-live='polite'></p>",
  "    </section>",
  "",
  "    <section class='screen tree-screen' id='tree-screen' data-stage='tree' hidden>",
  `      <button class='back-button' type='button' data-back-home><i class='ph ph-arrow-left' aria-hidden='true'></i> ${t("back")}</button>`,
  `      <h1 tabindex='-1'>${t("shakeTree")}</h1>`,
  "      <div class='tree-stage' id='tree-stage'>",
  `        <button class='tree-button' id='tree-button' type='button' aria-describedby='tree-instruction'><img src='./assets/generated/money-branch-v1.webp' alt='${t("treeAlt")}' width='640' height='960' /></button>`,
  "        <div class='shake-leaf-layer' id='shake-leaf-layer' aria-hidden='true'></div>",
  "        <div class='falling-leaves' id='falling-leaves' aria-hidden='true'>",
  "          <img class='falling-leaf leaf-one' src='./assets/generated/money-leaf-v1.webp' alt='' />",
  "          <img class='falling-leaf leaf-two' src='./assets/generated/money-leaf-v1.webp' alt='' />",
  "          <img class='falling-leaf leaf-three' src='./assets/generated/money-leaf-v1.webp' alt='' />",
  "          <img class='falling-leaf leaf-four' src='./assets/generated/money-leaf-v1.webp' alt='' />",
  "          <img class='falling-leaf leaf-five' src='./assets/generated/money-leaf-v1.webp' alt='' />",
  "          <img class='falling-leaf leaf-six' src='./assets/generated/money-leaf-v1.webp' alt='' />",
  "        </div>",
  "        <div class='wish-leaf' id='wish-leaf' hidden><img src='./assets/generated/money-leaf-v1.webp' alt='' aria-hidden='true' /><blockquote id='wish-text'></blockquote></div>",
  "      </div>",
  "      <div class='shake-panel'>",
  `        <p id='tree-instruction'>${t("tapOrSwipe")}</p>`,
  "        <strong id='shake-copy'>0 / 3</strong>",
  `        <button class='primary-button tree-continue' id='tree-continue' type='button' hidden>${t("keepWish")}</button>`,
  "      </div>",
  "      <section class='ritual-circle tree-circle' id='tree-circle' aria-labelledby='tree-circle-label' hidden>",
  `        <div class='circle-heading'><div><p id='tree-circle-label'></p><strong id='tree-circle-count'>0</strong></div><small>${t("topFive")}</small></div>`,
  "        <ol class='circle-list' id='tree-circle-list' role='list'></ol>",
  "      </section>",
  "      <p class='sr-only' id='tree-live' aria-live='polite'></p>",
  "    </section>",
  "",
  "    <section class='screen name-screen' id='name-screen' data-stage='name' hidden>",
  `      <p class='step-kicker' id='name-step'>${t("stepOne")}</p>`,
  `      <h1 tabindex='-1' aria-describedby='name-step'>${t("nameTitle")}</h1>`,
  "      <form class='name-form' id='name-form'>",
  "        <label class='nickname-field' for='ritual-nickname'>",
  `          <span>${t("nickname")} <small>${t("optionalThisDevice")}</small></span>`,
  `          <input id='ritual-nickname' name='nickname' maxlength='20' autocomplete='name' placeholder='${t("nicknamePlaceholder")}' />`,
  "        </label>",
  `        <button class='primary-button' id='continue-name' type='submit'>${t("continue")}</button>`,
  "      </form>",
  "    </section>",
  "",
  "    <section class='screen feedback-screen' id='feedback-screen' data-stage='feedback' hidden>",
  `      <p class='step-kicker' id='feedback-step'>${t("stepTwo")}</p>`,
  `      <h1 tabindex='-1' aria-describedby='feedback-step'>${t("feedbackTitle")}</h1>`,
  "      <form class='feedback-form' id='feedback-form'>",
  "        <fieldset class='face-fieldset'>",
  `          <legend class='sr-only'>${t("ratingLegend")}</legend>`,
  "          <div class='face-picker'>",
  `            <label><input type='radio' name='rating' value='1' aria-label='${t("rating1")}' /><span><i class='ph-fill ph-smiley-sad' aria-hidden='true'></i><small>1</small></span></label>`,
  `            <label><input type='radio' name='rating' value='2' aria-label='${t("rating2")}' /><span><i class='ph-fill ph-smiley-nervous' aria-hidden='true'></i><small>2</small></span></label>`,
  `            <label><input type='radio' name='rating' value='3' aria-label='${t("rating3")}' /><span><i class='ph-fill ph-smiley-meh' aria-hidden='true'></i><small>3</small></span></label>`,
  `            <label><input type='radio' name='rating' value='4' aria-label='${t("rating4")}' /><span><i class='ph-fill ph-smiley' aria-hidden='true'></i><small>4</small></span></label>`,
  `            <label><input type='radio' name='rating' value='5' aria-label='${t("rating5")}' /><span><i class='ph-fill ph-smiley-wink' aria-hidden='true'></i><small>5</small></span></label>`,
  "          </div>",
  "        </fieldset>",
  "        <label class='comment-field' for='feedback-note'>",
  `          <span>${t("comment")} <small>${t("optional")}</small></span>`,
  `          <textarea id='feedback-note' maxlength='280' rows='4' placeholder='${t("commentPlaceholder")}' aria-describedby='feedback-disclosure note-count'></textarea>`,
  "          <small class='sr-only' id='note-count'>0 / 280</small>",
  "        </label>",
  `        <p class='feedback-disclosure' id='feedback-disclosure' hidden>${t("feedbackDisclosure")}</p>`,
  "        <p class='feedback-status' id='feedback-status' role='status' aria-live='polite'></p>",
  `        <button class='primary-button' id='submit-feedback' type='submit' disabled>${t("done")}</button>`,
  `        <button class='text-button skip-feedback' id='skip-feedback' type='button' hidden>${t("continueWithoutSending")}</button>`,
  "      </form>",
  "    </section>",
  "",
  "    <section class='screen done-screen' id='done-screen' data-stage='done' hidden>",
  "      <div class='done-visual' id='done-visual' aria-hidden='true'>",
  "        <img class='done-frog' src='./assets/generated/money-frog-v1.webp' alt='' width='900' height='936' hidden />",
  "        <img class='done-tree' src='./assets/generated/money-branch-v1.webp' alt='' width='640' height='960' hidden />",
  "        <img class='done-leaf done-leaf-one' src='./assets/generated/money-leaf-v1.webp' alt='' />",
  "        <img class='done-leaf done-leaf-two' src='./assets/generated/money-leaf-v1.webp' alt='' />",
  "        <img class='done-leaf done-leaf-three' src='./assets/generated/money-leaf-v1.webp' alt='' />",
  "      </div>",
  `      <h1 tabindex='-1' id='done-title'>${t("ritualComplete")}</h1>`,
  "      <p id='done-message'></p>",
  `      <button class='primary-button' id='new-ritual-button' type='button'>${t("anotherRitual")}</button>`,
  `      <button class='text-button' id='clear-progress' type='button'>${t("clearProgress")}</button>`,
  "    </section>",
  "  </main>",
  "",
  "  <aside class='privacy-panel' id='privacy-panel' role='dialog' aria-modal='true' aria-labelledby='privacy-title' hidden>",
  `    <button class='close-panel' id='privacy-close' type='button' aria-label='${t("close")}'><i class='ph ph-x' aria-hidden='true'></i></button>`,
  `    <p class='panel-kicker'>${t("justForFun")}</p>`,
  `    <h2 id='privacy-title'>${t("privacyTitle")}</h2>`,
  `    <p>${t("privacyFrogTree")}</p>`,
  `    <p>${t("privacyStorage")}</p>`,
  `    <p>${t("privacyCircle")}</p>`,
  "  </aside>",
  "",
  `  <aside class='debug-panel' id='debug-panel' hidden aria-label='${t("debugInspectorLabel")}'>`,
  `    <div><strong>${t("qaInspector")}</strong><button id='debug-toggle' type='button' aria-expanded='true'>${t("hide")}</button></div>`,
  `    <dl><dt>${t("arm")}</dt><dd id='debug-arm'></dd><dt>${t("source")}</dt><dd id='debug-source'></dd></dl>`,
  "    <pre id='debug-events' tabindex='0'></pre>",
  "  </aside>",
  "</div>",
].join("\n");

type Stage = "home" | "amount" | "frog" | "result" | "tree" | "name" | "feedback" | "done";
type Ritual = "frog" | "tree";

let currentStage: Stage = "home";
let completedRitual: Ritual | null = null;
let feedbackSubmitting = false;
let currentPot: PotRecord | null = null;
let preparedSplit: SplitResult | null = null;
let currentWish: WishMessage | null = null;
let ritualStartedAt = performance.now();
let frogStartedAt = performance.now();
let patCount = 0;
let splitRevealed = false;
let keepDecisionMade = false;
let shakeCount = 0;
let treeRevealed = false;
let treeRunCounter = 0;
let gestureMoved = false;
let lastGestureX: number | null = null;
let lastGestureDirection = 0;
let lastGestureShakeAt = 0;
let cancelPatMotion: (() => void) | null = null;
let cancelTreeShakeMotion: (() => void) | null = null;

const potForm = required<HTMLFormElement>("#pot-form");
const amountInput = required<HTMLInputElement>("#pot-amount");
const amountError = required<HTMLElement>("#amount-error");
const beginButton = required<HTMLButtonElement>("#begin-button");
const frogButton = required<HTMLButtonElement>("#frog-button");
const frogLive = required<HTMLElement>("#frog-live");
const patCopy = required<HTMLElement>("#pat-copy");
const bubbleField = required<HTMLElement>("#bubble-field");
const splitLive = required<HTMLElement>("#split-live");
const treeButton = required<HTMLButtonElement>("#tree-button");
const treeLive = required<HTMLElement>("#tree-live");
const nameForm = required<HTMLFormElement>("#name-form");
const feedbackForm = required<HTMLFormElement>("#feedback-form");
const feedbackNote = required<HTMLTextAreaElement>("#feedback-note");
const feedbackDisclosure = required<HTMLElement>("#feedback-disclosure");
const feedbackStatus = required<HTMLElement>("#feedback-status");
const feedbackSubmit = required<HTMLButtonElement>("#submit-feedback");
const skipFeedback = required<HTMLButtonElement>("#skip-feedback");
const nicknameInput = required<HTMLInputElement>("#ritual-nickname");

renderRitualCircle("frog");
renderRitualCircle("tree");
renderSavedNickname();

configureHome();
configureAmount();
configureFrog();
configureResult();
configureTree();
configureName();
configureFeedback();
configureUtilities();
configureDebug();

analytics.track("demo_view", {
  viewport_class: viewportClass(),
  reduced_motion: reducedMotion,
});

function configureHome(): void {
  required<HTMLButtonElement>("#choose-frog").addEventListener("click", () => {
    analytics.track("ritual_selected", { ritual: "frog" });
    setStage("amount");
  });
  required<HTMLButtonElement>("#choose-tree").addEventListener("click", () => {
    analytics.track("ritual_selected", { ritual: "tree" });
    startTree();
  });
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-back-home]")) {
    button.addEventListener("click", goHome);
  }
  required<HTMLButtonElement>("#home-link").addEventListener("click", () => {
    if (currentStage !== "home") goHome();
  });
}

function configureAmount(): void {
  for (const preset of document.querySelectorAll<HTMLButtonElement>(".preset")) {
    preset.addEventListener("click", () => {
      amountInput.value = preset.dataset.amount ?? "";
      amountInput.dispatchEvent(new Event("input", { bubbles: true }));
      amountInput.focus();
    });
  }
  amountInput.addEventListener("input", validateAmount);
  amountInput.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    potForm.requestSubmit();
  });
  potForm.addEventListener("submit", (event) => {
    event.preventDefault();
    const amountCents = parseAmount(amountInput.value);
    if (amountCents === null) {
      validateAmount();
      return;
    }
    ritualStartedAt = performance.now();
    frogStartedAt = performance.now();
    completedRitual = "frog";
    currentPot = createPot(session.state, amountCents, amountBand(amountCents));
    preparedSplit = createSplit(amountCents, factor.frog);
    analytics.track("frog_started", {
      frog_factor: factor.frog,
      amount_band: amountBand(amountCents),
      currency_code: currency.code,
    });
    setStage("frog");
  });
  validateAmount();
}

function validateAmount(): void {
  const amountCents = parseAmount(amountInput.value);
  const invalid = amountInput.value.trim().length > 0 && amountCents === null;
  amountError.textContent = invalid
    ? t("amountError", { min: formatMoney(2_000), max: formatMoney(20_000) })
    : "";
  amountInput.setAttribute("aria-invalid", String(invalid));
  beginButton.disabled = amountCents === null;
  for (const preset of document.querySelectorAll<HTMLButtonElement>(".preset")) {
    preset.classList.toggle("is-selected", preset.dataset.amount === amountInput.value);
  }
}

function configureFrog(): void {
  frogButton.addEventListener("click", (event) => {
    if (patCount >= 5 || !currentPot || !preparedSplit) return;
    patCount += 1;
    analytics.track("frog_tap", { tap_count: patCount });
    updatePatProgress();
    playPatMotion(event);
    if (patCount === 5) showBubbles();
  });
  for (const bubble of document.querySelectorAll<HTMLButtonElement>(".ritual-bubble")) {
    bubble.addEventListener("click", () => revealSplit(Number(bubble.dataset.bubble)));
  }
}

function updatePatProgress(): void {
  const dots = document.querySelectorAll<HTMLElement>("#pat-progress span");
  dots.forEach((dot, index) => dot.classList.toggle("is-complete", index < patCount));
  patCopy.textContent = String(patCount) + " / 5";
  frogLive.textContent = t("patStatus", { count: patCount });
}

function playPatMotion(event: MouseEvent): void {
  if (reducedMotion) return;
  cancelPatMotion?.();
  const direction = patCount % 2 === 0 ? -1 : 1;
  const playback = animate(
    frogButton,
    {
      scale: [1, 0.88, 1.06, 1],
      y: [0, 7, -5, 0],
      rotate: [0, direction * 3.5, direction * -1.5, 0],
    },
    { duration: 0.44, ease: [0.2, 0.82, 0.2, 1] },
  );
  cancelPatMotion = () => playback.cancel();
  showPatRipple(event);
}

function showPatRipple(event: MouseEvent): void {
  const layer = required<HTMLElement>("#pat-effect-layer");
  const rect = layer.getBoundingClientRect();
  const usedPointer = event.detail > 0 && event.clientX >= rect.left && event.clientX <= rect.right;
  const x = usedPointer ? event.clientX - rect.left : rect.width * 0.5;
  const y = usedPointer && event.clientY >= rect.top && event.clientY <= rect.bottom
    ? event.clientY - rect.top
    : rect.height * 0.56;
  const ripple = document.createElement("span");
  ripple.className = "pat-ripple";
  ripple.style.left = String(x) + "px";
  ripple.style.top = String(y) + "px";
  layer.append(ripple);
  animate(
    ripple,
    { opacity: [0, 0.9, 0], scale: [0.2, 1, 1.55], y: [0, -5, -16] },
    { duration: 0.58, ease: [0.16, 0.84, 0.24, 1] },
  );
  window.setTimeout(() => ripple.remove(), 700);
}

function showBubbles(): void {
  frogButton.disabled = true;
  frogButton.classList.add("is-mouth-open");
  required<HTMLElement>("#frog-title").textContent = t("popOne");
  required<HTMLElement>("#frog-instruction").textContent = t("chooseBubble");
  frogLive.textContent = t("bubblesLive");
  bubbleField.hidden = false;
  const bubbles = Array.from(bubbleField.querySelectorAll<HTMLButtonElement>("button"));
  if (reducedMotion) {
    bubbles.forEach((bubble) => (bubble.style.opacity = "1"));
    bubbles[0]?.focus();
    return;
  }
  window.requestAnimationFrame(() => {
    const mouth = required<HTMLElement>("#mouth-anchor").getBoundingClientRect();
    const mouthX = mouth.left + mouth.width / 2;
    const mouthY = mouth.top + mouth.height / 2;
    bubbles.forEach((bubble, index) => {
      const rect = bubble.getBoundingClientRect();
      const deltaX = mouthX - (rect.left + rect.width / 2);
      const deltaY = mouthY - (rect.top + rect.height / 2);
      animate(
        bubble,
        {
          opacity: [0, 1],
          scale: [0.12, 1.08, 1],
          x: [deltaX, deltaX * 0.3, 0],
          y: [deltaY, deltaY * 0.25, 0],
        },
        {
          delay: index * 0.1,
          duration: 0.75,
          ease: [0.18, 0.88, 0.25, 1],
        },
      );
    });
    window.setTimeout(() => bubbles[0]?.focus(), 650);
  });
}

function revealSplit(bubbleIndex: number): void {
  if (!currentPot || !preparedSplit || splitRevealed || bubbleIndex < 1 || bubbleIndex > 3) return;
  splitRevealed = true;
  for (const bubble of document.querySelectorAll<HTMLButtonElement>(".ritual-bubble")) bubble.disabled = true;
  currentPot = setPotSplit(session.state, currentPot.id, preparedSplit, bubbleIndex);
  analytics.track("bubble_selected", { bubble_index: bubbleIndex });
  analytics.track("frog_completed", {
    frog_factor: factor.frog,
    enjoy_percent: preparedSplit.enjoyPercent,
    duration_band: durationBand(performance.now() - frogStartedAt),
  });
  required<HTMLElement>("#enjoy-amount").textContent = formatMoney(preparedSplit.enjoyCents);
  required<HTMLElement>("#keep-amount").textContent = formatMoney(preparedSplit.keepCents);
  required<HTMLElement>("#enjoy-percent").textContent = String(preparedSplit.enjoyPercent) + "%";
  required<HTMLElement>("#keep-percent").textContent = String(preparedSplit.keepPercent) + "%";
  splitLive.textContent = t("splitLive", {
    enjoy: formatMoney(preparedSplit.enjoyCents),
    keep: formatMoney(preparedSplit.keepCents),
  });
  celebrate(24);
  setStage("result");
}

function configureResult(): void {
  required<HTMLButtonElement>("#confirm-keep").addEventListener("click", () => finishKeepChoice(true));
  required<HTMLButtonElement>("#decline-keep").addEventListener("click", () => finishKeepChoice(false));
}

function finishKeepChoice(confirmed: boolean): void {
  if (!currentPot || !preparedSplit || keepDecisionMade) return;
  keepDecisionMade = true;
  currentPot = setPotKeepDecision(session.state, currentPot.id, confirmed);
  analytics.track("keep_confirmed", { frog_factor: factor.frog, confirmed });
  prepareNameStep("frog");
}

function configureTree(): void {
  treeButton.addEventListener("click", () => {
    if (gestureMoved) {
      gestureMoved = false;
      return;
    }
    registerShake();
  });
  treeButton.addEventListener("pointerdown", (event) => {
    lastGestureX = event.clientX;
    lastGestureDirection = 0;
    gestureMoved = false;
    treeButton.setPointerCapture(event.pointerId);
  });
  treeButton.addEventListener("pointermove", (event) => {
    if (lastGestureX === null || treeRevealed) return;
    const distance = event.clientX - lastGestureX;
    if (Math.abs(distance) < 18) return;
    const direction = Math.sign(distance);
    gestureMoved = true;
    const now = performance.now();
    if (direction !== lastGestureDirection && now - lastGestureShakeAt > 140) {
      lastGestureDirection = direction;
      lastGestureShakeAt = now;
      lastGestureX = event.clientX;
      registerShake();
    }
  });
  const endGesture = (): void => {
    lastGestureX = null;
    lastGestureDirection = 0;
  };
  treeButton.addEventListener("pointerup", endGesture);
  treeButton.addEventListener("pointercancel", () => {
    endGesture();
    gestureMoved = false;
  });
  required<HTMLButtonElement>("#tree-continue").addEventListener("click", () => prepareNameStep("tree"));
}

function startTree(): void {
  ritualStartedAt = performance.now();
  completedRitual = "tree";
  treeRunCounter += 1;
  currentWish = chooseWish(
    session.state.participantId,
    localDay(),
    session.state.potCounter + treeRunCounter,
  );
  required<HTMLElement>("#shake-leaf-layer").replaceChildren();
  required<HTMLElement>("#tree-circle").hidden = true;
  analytics.track("wish_started", { wish_factor: "tactile" });
  setStage("tree");
}

function registerShake(): void {
  if (treeRevealed || !currentWish || shakeCount >= 3) return;
  shakeCount += 1;
  const progress = ([25, 50, 100] as const)[shakeCount - 1] ?? 100;
  analytics.track("branch_progress", { progress_band: progress });
  required<HTMLElement>("#shake-copy").textContent = String(shakeCount) + " / 3";
  treeLive.textContent = t("shakeStatus", { count: shakeCount });
  if (!reducedMotion) {
    playTreeShakeMotion();
    releaseShakeLeaves(shakeCount);
  }
  if (shakeCount === 3) {
    treeButton.disabled = true;
    window.setTimeout(revealTreeWish, reducedMotion ? 0 : 560);
  }
}

function playTreeShakeMotion(): void {
  cancelTreeShakeMotion?.();
  const direction = shakeCount % 2 === 0 ? -1 : 1;
  const playback = animate(
    treeButton,
    {
      rotate: [0, direction * -6, direction * 6, direction * -4, direction * 3, 0],
      x: [0, direction * -6, direction * 7, direction * -4, direction * 2, 0],
      scale: [1, 1.012, 1],
    },
    { duration: 0.52, ease: [0.42, 0, 0.58, 1] },
  );
  cancelTreeShakeMotion = () => playback.cancel();
}

function releaseShakeLeaves(acceptedShake: number): void {
  const layer = required<HTMLElement>("#shake-leaf-layer");
  const fallDistance = Math.max(270, layer.clientHeight * 0.82);
  const driftUnit = Math.max(32, layer.clientWidth * 0.1);
  const leftPositions = [
    [24, 58],
    [34, 70],
    [18, 52],
  ][acceptedShake - 1] ?? [30, 62];
  const driftFactors = [
    [-0.9, 1.05],
    [0.95, -1.1],
    [-0.75, 1.15],
  ][acceptedShake - 1] ?? [-0.85, 1];

  leftPositions.forEach((left, index) => {
    const drift = (driftFactors[index] ?? (index === 0 ? -0.85 : 1)) * driftUnit;
    const leaf = document.createElement("img");
    leaf.className = "shake-leaf";
    leaf.src = "./assets/generated/money-leaf-v1.webp";
    leaf.alt = "";
    leaf.width = 512;
    leaf.height = 512;
    leaf.style.left = String(left) + "%";
    leaf.style.top = String(10 + index * 9) + "%";
    layer.append(leaf);
    animate(
      leaf,
      {
        opacity: [0, 1, 1, 0],
        x: [0, drift * 0.35, drift],
        y: [-8, fallDistance * 0.38, fallDistance],
        rotate: [index * 24, 145 + acceptedShake * 28, 330 + index * 72],
        scale: [0.24, 0.54, 0.42],
      },
      {
        delay: index * 0.05,
        duration: 0.9 + index * 0.05,
        ease: [0.32, 0, 0.82, 1],
      },
    );
    window.setTimeout(() => leaf.remove(), 1_100);
  });
}

function revealTreeWish(): void {
  if (treeRevealed || !currentWish) return;
  treeRevealed = true;
  analytics.track("wish_revealed", {
    wish_factor: "tactile",
    message_id: currentWish.id,
    message_tone: currentWish.tone,
  });
  const localizedWish = translateWish(locale, currentWish.id);
  required<HTMLElement>("#tree-instruction").textContent = t("yourWish");
  const wishText = required<HTMLElement>("#wish-text");
  wishText.textContent = localizedWish;
  wishText.dataset.fit = wishTextFit(localizedWish);
  const wishLeaf = required<HTMLElement>("#wish-leaf");
  const continueButton = required<HTMLButtonElement>("#tree-continue");
  const treeCircle = required<HTMLElement>("#tree-circle");
  treeButton.classList.add("is-wish-revealed");
  wishLeaf.hidden = false;
  continueButton.hidden = false;
  treeCircle.hidden = false;
  treeLive.textContent = t("wishLive", { wish: localizedWish });
  const leaves = Array.from(document.querySelectorAll<HTMLElement>(".falling-leaf"));
  if (reducedMotion) {
    leaves.forEach((leaf) => (leaf.style.display = "none"));
    wishLeaf.style.opacity = "1";
    continueButton.focus();
    return;
  }
  animate(treeButton, { rotate: [0, -6, 7, -5, 4, 0], scale: [1, 1.02, 1] }, { duration: 0.85 });
  leaves.forEach((leaf, index) => {
    const drift = [-72, 54, -30, 80, -58, 36][index] ?? 0;
    animate(
      leaf,
      {
        opacity: [0, 1, 1, 0],
        x: [0, drift * 0.4, drift],
        y: [-30, 160, 430],
        rotate: [index * 12, 160 + index * 28, 340 + index * 46],
        scale: [0.45, 0.7, 0.52],
      },
      { delay: index * 0.07, duration: 1.55 + index * 0.06, ease: [0.42, 0, 1, 1] },
    );
  });
  animate(
    wishLeaf,
    { opacity: [0, 1], scale: [0.18, 1.08, 1], y: [-130, 8, 0], rotate: [-28, 3, 0] },
    { delay: 0.48, duration: 1.05, ease: [0.16, 0.86, 0.24, 1] },
  );
  celebrate(28);
  window.setTimeout(() => continueButton.focus(), 1350);
}

function configureName(): void {
  nameForm.addEventListener("submit", (event) => {
    event.preventDefault();
    setNickname(session.state, nicknameInput.value);
    renderSavedNickname();
    prepareFeedback();
  });
}

function prepareNameStep(ritual: Ritual): void {
  completedRitual = ritual;
  nameForm.reset();
  nicknameInput.value = session.state.nickname ?? "";
  setStage("name");
}

function configureFeedback(): void {
  feedbackForm.addEventListener("change", validateFeedback);
  feedbackNote.addEventListener("input", () => {
    required<HTMLElement>("#note-count").textContent = String(feedbackNote.value.length) + " / 280";
  });
  feedbackForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    const rating = readRating();
    if (rating === null || !completedRitual || feedbackSubmitting) return;

    if (!firebase.feedbackEnabled) {
      clearFeedbackComment();
      completeExperience();
      return;
    }

    feedbackSubmitting = true;
    feedbackStatus.textContent = "";
    skipFeedback.hidden = true;
    feedbackSubmit.textContent = t("sendingFeedback");
    validateFeedback();

    const result = await firebase.submitFeedback({
      rating,
      ritual: completedRitual,
      locale,
      arm: session.state.arm,
      source,
      demoVersion: DEMO_VERSION,
      comment: feedbackNote.value,
    });

    if (result.status === "sent") {
      analytics.track("feedback_submitted", { rating, ritual: completedRitual });
      feedbackStatus.textContent = t("feedbackSent");
      clearFeedbackComment();
      completeExperience();
      return;
    }

    if (result.status === "disabled") {
      clearFeedbackComment();
      completeExperience();
      return;
    }

    feedbackSubmitting = false;
    feedbackStatus.textContent = t("feedbackSendError");
    feedbackSubmit.textContent = t("retryFeedback");
    skipFeedback.hidden = false;
    validateFeedback();
  });
  skipFeedback.addEventListener("click", () => {
    if (feedbackSubmitting) return;
    clearFeedbackComment();
    completeExperience();
  });
}

function prepareFeedback(): void {
  feedbackForm.reset();
  clearFeedbackComment();
  feedbackSubmitting = false;
  feedbackDisclosure.hidden = !firebase.feedbackEnabled;
  feedbackStatus.textContent = "";
  skipFeedback.hidden = true;
  feedbackSubmit.textContent = firebase.feedbackEnabled ? t("sendFeedback") : t("done");
  validateFeedback();
  setStage("feedback");
}

function validateFeedback(): void {
  feedbackSubmit.disabled = feedbackSubmitting || readRating() === null;
}

function clearFeedbackComment(): void {
  feedbackNote.value = "";
  required<HTMLElement>("#note-count").textContent = "0 / 280";
}

function readRating(): number | null {
  const rating = Number(new FormData(feedbackForm).get("rating"));
  return [1, 2, 3, 4, 5].includes(rating) ? rating : null;
}

function completeExperience(): void {
  if (!completedRitual) return;
  analytics.track("phase_completed", {
    duration_band: durationBand(performance.now() - ritualStartedAt),
  });
  const doneFrog = required<HTMLImageElement>(".done-frog");
  const doneTree = required<HTMLImageElement>(".done-tree");
  const doneTitle = required<HTMLElement>("#done-title");
  const doneMessage = required<HTMLElement>("#done-message");
  const doneLeaves = document.querySelectorAll<HTMLElement>(".done-leaf");
  if (completedRitual === "frog") {
    doneFrog.hidden = false;
    doneTree.hidden = true;
    doneLeaves.forEach((leaf) => (leaf.hidden = true));
    doneTitle.textContent = t("frogApproved");
    doneMessage.textContent = t("doneFrogMessage");
  } else {
    doneFrog.hidden = true;
    doneTree.hidden = false;
    doneLeaves.forEach((leaf) => (leaf.hidden = false));
    doneTitle.textContent = t("wishCollected");
    doneMessage.textContent = currentWish ? translateWish(locale, currentWish.id) : t("wishFallback");
  }
  setStage("done");
  playDoneMotion();
}

function renderRitualCircle(ritual: CircleRitual): void {
  const snapshot = SAMPLE_CIRCLES[ritual];
  required<HTMLElement>(`#${ritual}-circle-label`).textContent = t("circleLabel");
  required<HTMLElement>(`#${ritual}-circle-count`).textContent = formatPeopleCount(locale, snapshot.peopleCount);
  const list = required<HTMLOListElement>(`#${ritual}-circle-list`);
  list.replaceChildren();
  snapshot.leaders.forEach((leader, index) => {
    const item = document.createElement("li");
    const rank = document.createElement("span");
    const name = document.createElement("strong");
    const count = document.createElement("span");
    rank.className = "circle-rank";
    name.className = "circle-name";
    count.className = "circle-count";
    rank.textContent = String(index + 1);
    name.textContent = leader.names[locale];
    count.textContent = formatRitualCount(locale, leader.ritualCount);
    item.append(rank, name, count);
    list.append(item);
  });
}

function renderSavedNickname(): void {
  const chip = required<HTMLElement>("#profile-chip");
  const nickname = session.state.nickname;
  chip.hidden = !nickname;
  chip.textContent = nickname ?? "";
  if (nickname) {
    chip.setAttribute("aria-label", t("savedNickname", { nickname }));
    chip.setAttribute("title", t("savedAs", { nickname }));
  } else {
    chip.removeAttribute("aria-label");
    chip.removeAttribute("title");
  }
}

function playDoneMotion(): void {
  if (reducedMotion) return;
  const activeArt = completedRitual === "frog" ? required<HTMLElement>(".done-frog") : required<HTMLElement>(".done-tree");
  animate(activeArt, { opacity: [0, 1], scale: [0.72, 1.08, 1], y: [32, -8, 0] }, { duration: 0.9, ease: [0.16, 0.86, 0.24, 1] });
  if (completedRitual === "tree") {
    animate(
      Array.from(document.querySelectorAll<HTMLElement>(".done-leaf")),
      { opacity: [0, 1], scale: [0.2, 1], y: [-35, 0], rotate: [-40, 0] },
      { delay: stagger(0.11), duration: 0.7 },
    );
  }
  window.setTimeout(() => celebrate(46), 240);
}

function configureUtilities(): void {
  const privacyButton = required<HTMLButtonElement>("#privacy-button");
  const privacyPanel = required<HTMLElement>("#privacy-panel");
  const closePrivacy = (): void => {
    privacyPanel.hidden = true;
    privacyButton.setAttribute("aria-expanded", "false");
    privacyButton.focus();
  };
  privacyButton.addEventListener("click", () => {
    privacyPanel.hidden = false;
    privacyButton.setAttribute("aria-expanded", "true");
    required<HTMLButtonElement>("#privacy-close").focus();
  });
  required<HTMLButtonElement>("#privacy-close").addEventListener("click", closePrivacy);
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !privacyPanel.hidden) closePrivacy();
  });
  required<HTMLButtonElement>("#new-ritual-button").addEventListener("click", () => {
    window.location.reload();
  });
  required<HTMLButtonElement>("#clear-progress").addEventListener("click", () => {
    clearDemoState();
    const url = new URL(window.location.href);
    url.searchParams.delete("reset");
    window.location.assign(url.toString());
  });
}

function configureDebug(): void {
  if (!debug) return;
  const panel = required<HTMLElement>("#debug-panel");
  panel.hidden = false;
  required<HTMLElement>("#debug-arm").textContent = session.state.arm + " · " + t("frog") + " " + factor.frog;
  required<HTMLElement>("#debug-source").textContent = source;
  const renderEvents = (): void => {
    const latest = window.__MONEY_RITUALS_EVENTS__?.slice(-12) ?? [];
    required<HTMLElement>("#debug-events").textContent = JSON.stringify(latest, null, 2);
  };
  window.addEventListener("money-rituals:event", renderEvents);
  const toggle = required<HTMLButtonElement>("#debug-toggle");
  toggle.addEventListener("click", () => {
    const collapsed = panel.classList.toggle("is-collapsed");
    toggle.textContent = collapsed ? t("show") : t("hide");
    toggle.setAttribute("aria-expanded", String(!collapsed));
  });
  renderEvents();
}

function goHome(): void {
  if (currentStage !== "home") window.location.reload();
}

function setStage(stage: Stage): void {
  currentStage = stage;
  for (const screen of document.querySelectorAll<HTMLElement>(".screen")) {
    const active = screen.dataset.stage === stage;
    screen.hidden = !active;
    screen.classList.toggle("is-active", active);
  }
  window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });
  const heading = required<HTMLElement>("#" + stage + "-screen h1");
  window.setTimeout(() => heading.focus({ preventScroll: true }), reducedMotion ? 0 : 120);
  if (!reducedMotion) {
    animate(required<HTMLElement>("#" + stage + "-screen"), { opacity: [0, 1], y: [14, 0] }, { duration: 0.42, ease: [0.2, 0.8, 0.2, 1] });
  }
}

function celebrate(particleCount: number): void {
  if (reducedMotion) return;
  confetti({
    particleCount,
    spread: 64,
    startVelocity: 22,
    gravity: 0.72,
    scalar: 0.75,
    ticks: 120,
    origin: { y: 0.48 },
    colors: ["#f5d57a", "#79c992", "#d9a43e", "#fff2d4"],
    disableForReducedMotion: true,
  });
}

function parseAmount(raw: string): number | null {
  const normalized = normalizeLocalizedDecimal(raw);
  if (!/^\d{1,3}(?:\.\d{1,2})?$/.test(normalized)) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value) || value < 20 || value > 200) return null;
  return Math.round(value * 100);
}

function wishTextFit(copy: string): "regular" | "snug" | "compact" {
  const characterCount = Array.from(copy).length;
  if (characterCount >= 76) return "compact";
  if (characterCount >= 58) return "snug";
  return "regular";
}

function formatMoney(cents: number): string {
  return formatCurrency(locale, cents);
}

function viewportClass(): "mobile" | "tablet" | "desktop" {
  if (window.innerWidth < 640) return "mobile";
  if (window.innerWidth < 1024) return "tablet";
  return "desktop";
}

function localDay(): string {
  const now = new Date();
  return String(now.getFullYear()) + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");
}

function createSessionId(): string {
  const bytes = new Uint8Array(10);
  crypto.getRandomValues(bytes);
  return "s_" + Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function required<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error("Required element not found: " + selector);
  return element;
}
